import { readonly, type Ref } from 'vue';
import { canonicalLocale } from './locale';
import type { I18nPersist } from './persist';
import { activeLocale, configurePersist, setLocale } from './state';

export interface SetupI18nOptions {
    /** The Active locale of a visitor who has made no choice yet, regional: `nl-NL`. */
    initialLocale: string;
    /** Where a choice made with `setLocale` is kept; without it, choices last until the page reloads. */
    persist?: I18nPersist;
}

const tryCanonical = (tag: string | null | undefined): string | undefined => {
    if (!tag) return undefined;
    try {
        return canonicalLocale(tag);
    } catch {
        return undefined;
    }
};

const urlLocale = () => (typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('locale'));

/**
 * Sets the Active locale once, at startup: the URL's `?locale=`, else `persist.load()`, else
 * `initialLocale`. A candidate that is not a locale tag is skipped. Resolving saves nothing.
 */
export function setupI18n(options: SetupI18nOptions): void {
    const initial = canonicalLocale(options.initialLocale);
    configurePersist(options.persist);
    activeLocale.value = tryCanonical(urlLocale()) ?? tryCanonical(options.persist?.load()) ?? initial;
}

const locale = readonly(activeLocale);

/** The Active locale (`undefined` before `setupI18n`) and the way to change it. */
export function useLocale(): { locale: Readonly<Ref<string | undefined>>; setLocale: (locale: string) => void } {
    return { locale, setLocale };
}
